import { createSelector } from "reselect";
import { Map } from "immutable";
import { format, subDays, addDays, parse, parseISO } from "date-fns";

import { CALENDAR_WEEK_SHIFT } from "@typings/booking";

import { DATE_FORMAT } from "@services/DateFormatter";
import mapToList from "@services/MapToList";

import { IStore } from "@app/redux/reducers";

const getPricingFunction = (store: IStore) => store.endOfTenancy.get("pricing");

export const getPricing = createSelector(getPricingFunction, f => f);

const getIsPricingFetchedFunction = (store: IStore) =>
  store.endOfTenancy.get("isPricingFetched");

export const getIsPricingFetched = createSelector(
  getIsPricingFetchedFunction,
  f => f,
);

const getWarrantyFunction = (store: IStore) =>
  store.endOfTenancy.get("warranty");

export const getWarranty = createSelector(getWarrantyFunction, f => f);

const getSkipAgentAssignmentFunction = (store: IStore) =>
  store.endOfTenancy.get("skipAgentAssignment");

export const getSkipAgentAssignment = createSelector(
  getSkipAgentAssignmentFunction,
  f => f,
);

const getEndOfTenancyAvailabilityFunction = (store: IStore) =>
  store.endOfTenancy.get("calendar");

export const getEndOfTenancyAvailability = createSelector(
  getEndOfTenancyAvailabilityFunction,
  f => f,
);
const getEndOfTenancyAvailabilityV2Function = (store: IStore) =>
  store.endOfTenancy.get("calendarV2");

export const getEndOfTenancyAvailabilityV2 = createSelector(
  getEndOfTenancyAvailabilityV2Function,
  f => f,
);

const getWeekStartDateFunction = (store: IStore, week: CALENDAR_WEEK_SHIFT) => {
  const weekStartDate = store.endOfTenancy.getIn(["calendar", "weekStartDate"]);
  const currentWeek = parseISO(weekStartDate);

  // invalid date case
  if (isNaN(currentWeek.getTime())) {
    return "";
  }

  return week === CALENDAR_WEEK_SHIFT.PREVIOUS
    ? format(subDays(currentWeek, 7), DATE_FORMAT.YEAR_MONTH_DAY)
    : format(addDays(currentWeek, 7), DATE_FORMAT.YEAR_MONTH_DAY);
};

export const getWeekStartDate = createSelector(
  getWeekStartDateFunction,
  f => f,
);

const getWeekStartDateV2Function = (store: IStore) =>
  store.endOfTenancy.get("calendarV2").get("weekStartDate");

export const getWeekStartDateV2 = createSelector(
  getWeekStartDateV2Function,
  f => f,
);

const getTasksFunction = (store: IStore) =>
  store.endOfTenancy.get("tasks").toSeq();

export const getTasks = createSelector(getTasksFunction, f => f);

const getSelectedTasksListFunction = (store: IStore) =>
  mapToList(Map(getTasks(store))).filter(task => task.get("selected"));

export const getSelectedTasksList = createSelector(
  getSelectedTasksListFunction,
  f => f,
);

const getWarrantyPriceAndCurrencyFunction = (store: IStore) =>
  store.endOfTenancy.get("warrantyPriceAndCurrency");

export const getWarrantyPriceAndCurrency = createSelector(
  getWarrantyPriceAndCurrencyFunction,
  f => f,
);

const getExecutionDateFunction = (store: IStore) =>
  store.endOfTenancy.get("executionDate");

export const getExecutionDate = createSelector(
  getExecutionDateFunction,
  f => f,
);

export const getExecutionDateAsDateObject = createSelector(
  getExecutionDateFunction,
  date => parse(date, DATE_FORMAT.YEAR_MONTH_DAY, new Date()),
);

const getRequestDifferentStartingHourFunction = (store: IStore) =>
  store.endOfTenancy.get("requestDifferentStartingHour");

export const getRequestDifferentStartingHour = createSelector(
  getRequestDifferentStartingHourFunction,
  f => f,
);

const getSquareMetersFunction = (store: IStore) =>
  store.endOfTenancy.get("squareMeters");

export const getSquareMeters = createSelector(getSquareMetersFunction, f => f);

const getShowOfferFunction = (store: IStore) =>
  store.endOfTenancy.get("showOffer");

export const getShowOffer = createSelector(getShowOfferFunction, f => f);

const getServiceLocationIdFunction = (store: IStore) =>
  store.endOfTenancy.get("serviceLocationId");

export const getServiceLocationId = createSelector(
  getServiceLocationIdFunction,
  f => f,
);

const getAvailablePaymentMethodsFunction = (store: IStore) =>
  store.endOfTenancy.get("availablePaymentMethods");

export const getAvailablePaymentMethods = createSelector(
  getAvailablePaymentMethodsFunction,
  f => f,
);

const getSelectedPaymentMethodFunction = (store: IStore) =>
  store.endOfTenancy.get("selectedPaymentMethod");

export const getSelectedPaymentMethod = createSelector(
  getSelectedPaymentMethodFunction,
  f => f,
);

const getSpecialInstructionsFunction = (store: IStore) =>
  store.endOfTenancy.get("specialInstructions");

export const getSpecialInstructions = createSelector(
  getSpecialInstructionsFunction,
  f => f,
);

const getClientRegistrationFormFunction = (store: IStore) =>
  store.endOfTenancy.get("form");

export const getClientRegistrationForm = createSelector(
  getClientRegistrationFormFunction,
  f => f,
);

const getMainLocationUuidFunction = (store: IStore) =>
  store.endOfTenancy.get("mainLocationUuid");

export const getMainLocationUuid = createSelector(
  getMainLocationUuidFunction,
  f => f,
);

const getLocationUuidFunction = (store: IStore) =>
  store.endOfTenancy.get("locationUuid");

export const getLocationUuid = createSelector(getLocationUuidFunction, f => f);

const getReservationUuidFunction = (store: IStore) =>
  store.endOfTenancy.get("reservationUuid");

export const getReservationUuid = createSelector(
  getReservationUuidFunction,
  f => f,
);

const getVoucherFunction = (store: IStore) =>
  store.endOfTenancy.get("voucherToken");

export const getVoucher = createSelector(getVoucherFunction, f => f);

const getVoucherMessageFunction = (store: IStore) =>
  store.endOfTenancy.get("voucherMessage");

export const getVoucherMessage = createSelector(
  getVoucherMessageFunction,
  f => f,
);

const getVoucherStatusFunction = (store: IStore) =>
  store.endOfTenancy.get("voucherStatus");

export const getVoucherStatus = createSelector(
  getVoucherStatusFunction,
  f => f,
);

const getQueryParamsFunction = (store: IStore) =>
  store.endOfTenancy.get("queryParams");

export const getQueryParams = createSelector(getQueryParamsFunction, f => f);

const getPricingLoadingStateFn = (store: IStore) =>
  store.endOfTenancy.get("pricingLoadingState");

export const getPricingLoadingState = createSelector(
  getPricingLoadingStateFn,
  f => f,
);

const getAvailabilityLoadingStateFn = (store: IStore) =>
  store.endOfTenancy.get("availabilityLoadingState");

export const getAvailabilityLoadingState = createSelector(
  getAvailabilityLoadingStateFn,
  f => f,
);

const getCalendarCustomDaysFn = (store: IStore) =>
  store.endOfTenancy.get("calendarV2").get("staticDays");

export const getCalendarCustomDays = createSelector(
  getCalendarCustomDaysFn,
  f => f,
);
